import type { ReactNode } from 'react';
import { PLACEHOLDER_TOKEN_PATTERN } from '../placeholders/placeholder-token';
import {
  prepareInlineMarkupRuns,
  renderPreparedMarkupRuns,
} from './prepared-inline-markup';
import type { PreparedMarkupRun } from './prepared-inline-markup';

/**
 * One code point from a text or span run, or a whole `{br|}` run. Breaks are
 * atomic so a cut never halves one.
 */
type MarkupUnit =
  | { kind: 'char'; runIndex: number; offset: number; char: string }
  | { kind: 'break'; runIndex: number };

const isWhitespaceChar = (char: string): boolean => /\s/u.test(char);

const unitIsWhitespace = (unit: MarkupUnit): boolean =>
  unit.kind === 'break' || isWhitespaceChar(unit.char);

/**
 * Callers validate `limit` as a positive integer; still refuse anything else so
 * a bad budget never invents a cut.
 */
const isPositiveIntegerLimit = (limit: number): boolean =>
  typeof limit === 'number' && Number.isFinite(limit) && Number.isInteger(limit) && limit >= 1;

/**
 * Flattens prepared runs into the code-point stream a reader sees. Empty filled
 * words contribute nothing; each `{br|}` is one unit that counts as whitespace.
 */
const flattenUnits = (runs: PreparedMarkupRun[]): MarkupUnit[] => {
  const units: MarkupUnit[] = [];

  for (let runIndex = 0; runIndex < runs.length; runIndex += 1) {
    const run = runs[runIndex];
    if (run.kind === 'break') {
      units.push({ kind: 'break', runIndex });
      continue;
    }
    const codePoints = Array.from(run.words);
    for (let offset = 0; offset < codePoints.length; offset += 1) {
      units.push({ kind: 'char', runIndex, offset, char: codePoints[offset] });
    }
  }

  return units;
};

/**
 * Marks units that sit inside a whole `{{ … }}` token. Whitespace inside a
 * token is still whitespace for counting, but it must not be a cut point —
 * otherwise a composer-canvas budget can land on "Welcome back, {{…".
 */
const markUnitsInsidePlaceholderTokens = (
  runs: PreparedMarkupRun[],
  units: MarkupUnit[]
): boolean[] => {
  const inside = new Array<boolean>(units.length).fill(false);
  const unitIndexByRunOffset = new Map<string, number>();

  for (let unitIndex = 0; unitIndex < units.length; unitIndex += 1) {
    const unit = units[unitIndex];
    if (unit.kind === 'char') {
      unitIndexByRunOffset.set(`${unit.runIndex}:${unit.offset}`, unitIndex);
    }
  }

  for (let runIndex = 0; runIndex < runs.length; runIndex += 1) {
    const run = runs[runIndex];
    if (run.kind === 'break') {
      continue;
    }

    const tokenRe = new RegExp(PLACEHOLDER_TOKEN_PATTERN, 'g');
    let match: RegExpExecArray | null;
    while ((match = tokenRe.exec(run.words)) !== null) {
      const startCodePoint = Array.from(run.words.slice(0, match.index)).length;
      const tokenLength = Array.from(match[0]).length;
      for (let offset = startCodePoint; offset < startCodePoint + tokenLength; offset += 1) {
        const unitIndex = unitIndexByRunOffset.get(`${runIndex}:${offset}`);
        if (unitIndex !== undefined) {
          inside[unitIndex] = true;
        }
      }
    }
  }

  return inside;
};

/** First and exclusive-end indexes of the non-whitespace content, if any. */
const contentBounds = (units: MarkupUnit[]): { start: number; end: number } => {
  let start = 0;
  while (start < units.length && unitIsWhitespace(units[start])) {
    start += 1;
  }
  let end = units.length;
  while (end > start && unitIsWhitespace(units[end - 1])) {
    end -= 1;
  }
  return { start, end };
};

const hasNonWhitespace = (units: MarkupUnit[], from: number, to: number): boolean => {
  for (let index = from; index < to; index += 1) {
    if (!unitIsWhitespace(units[index])) {
      return true;
    }
  }
  return false;
};

/**
 * Visible length for budgeting: leading and trailing whitespace (including
 * `{br|}`) are ignored, and consecutive internal whitespace counts once,
 * matching how HTML shows it. Author whitespace is still kept verbatim in the
 * output.
 */
const visibleLength = (units: MarkupUnit[]): number => {
  const { start, end } = contentBounds(units);
  let count = 0;
  let inWhitespace = false;

  for (let index = start; index < end; index += 1) {
    if (unitIsWhitespace(units[index])) {
      if (!inWhitespace) {
        count += 1;
        inWhitespace = true;
      }
    } else {
      inWhitespace = false;
      count += 1;
    }
  }

  return count;
};

/** Visible-character cost of a unit span, with internal whitespace collapsed. */
const visibleCostOfSpan = (units: MarkupUnit[], from: number, to: number): number => {
  let count = 0;
  let inWhitespace = false;

  for (let index = from; index < to; index += 1) {
    if (unitIsWhitespace(units[index])) {
      if (!inWhitespace) {
        count += 1;
        inWhitespace = true;
      }
    } else {
      inWhitespace = false;
      count += 1;
    }
  }

  return count;
};

/**
 * Exclusive end index into `units` for the head. Leading whitespace costs
 * nothing and stays with the head. Keeps whole words within the budget; a
 * whole `{{ … }}` token is part of one unbreakable word (whitespace inside it
 * is never a cut point). If the first word alone is longer than `limit` and
 * contains no token, that word is cut at the budget (code points via
 * `Array.from`, never UTF-16 units); if it does contain a token, the whole
 * word stays with the head. Trailing whitespace at the cut stays out of the
 * head so the tail can start with it — including a `{br|}` at the cut.
 * Trailing whitespace after the last word is never part of the budget.
 */
const findHeadExclusiveEnd = (
  units: MarkupUnit[],
  limit: number,
  insidePlaceholder: boolean[]
): number => {
  const isCutPointWhitespace = (index: number): boolean =>
    unitIsWhitespace(units[index]) && !insidePlaceholder[index];

  let index = 0;
  while (index < units.length && isCutPointWhitespace(index)) {
    index += 1;
  }

  if (index >= units.length) {
    return 0;
  }

  let visible = 0;
  let headEnd = index;
  let wordsIncluded = 0;

  while (index < units.length) {
    const wsStart = index;
    while (index < units.length && isCutPointWhitespace(index)) {
      index += 1;
    }
    const hadWhitespace = index > wsStart;

    if (index >= units.length) {
      break;
    }

    const wordStart = index;
    while (index < units.length && !isCutPointWhitespace(index)) {
      index += 1;
    }
    const wordEnd = index;
    const wordLength = visibleCostOfSpan(units, wordStart, wordEnd);
    // Leading whitespace was skipped above and costs nothing; only gaps between
    // words spend budget.
    const whitespaceCost = wordsIncluded > 0 && hadWhitespace ? 1 : 0;
    const cost = whitespaceCost + wordLength;

    if (visible + cost <= limit) {
      headEnd = wordEnd;
      visible += cost;
      wordsIncluded += 1;
      continue;
    }

    if (wordsIncluded === 0) {
      let wordContainsToken = false;
      for (let unitIndex = wordStart; unitIndex < wordEnd; unitIndex += 1) {
        if (insidePlaceholder[unitIndex]) {
          wordContainsToken = true;
          break;
        }
      }
      // A token must stay whole; keeping the entire first word may leave the
      // tail empty, in which case the caller returns undefined.
      if (wordContainsToken) {
        return wordEnd;
      }
      return wordStart + (limit - visible);
    }

    return headEnd;
  }

  return headEnd;
};

/**
 * Rebuilds prepared runs so the head's words followed by the tail's equal the
 * full text. A span that straddles the cut becomes two spans with the same
 * marks, link, and styling.
 */
const splitPreparedRuns = (
  runs: PreparedMarkupRun[],
  units: MarkupUnit[],
  headExclusiveEnd: number
): { head: PreparedMarkupRun[]; tail: PreparedMarkupRun[] } => {
  const headCodePointEnd = runs.map(() => -1);
  const tailCodePointStart = runs.map(() => -1);
  const breakInHead = new Array<boolean>(runs.length).fill(false);
  const breakInTail = new Array<boolean>(runs.length).fill(false);

  for (let unitIndex = 0; unitIndex < units.length; unitIndex += 1) {
    const unit = units[unitIndex];
    const inHead = unitIndex < headExclusiveEnd;

    if (unit.kind === 'break') {
      if (inHead) {
        breakInHead[unit.runIndex] = true;
      } else {
        breakInTail[unit.runIndex] = true;
      }
      continue;
    }

    if (inHead) {
      headCodePointEnd[unit.runIndex] = unit.offset + 1;
    } else if (tailCodePointStart[unit.runIndex] === -1) {
      tailCodePointStart[unit.runIndex] = unit.offset;
    }
  }

  const head: PreparedMarkupRun[] = [];
  const tail: PreparedMarkupRun[] = [];

  for (let runIndex = 0; runIndex < runs.length; runIndex += 1) {
    const run = runs[runIndex];

    if (run.kind === 'break') {
      if (breakInHead[runIndex]) {
        head.push(run);
      }
      if (breakInTail[runIndex]) {
        tail.push(run);
      }
      continue;
    }

    const codePoints = Array.from(run.words);
    const headEnd = headCodePointEnd[runIndex];
    const tailStart = tailCodePointStart[runIndex];

    if (headEnd > 0) {
      head.push({ ...run, words: codePoints.slice(0, headEnd).join('') });
    }
    if (tailStart >= 0) {
      tail.push({ ...run, words: codePoints.slice(tailStart).join('') });
    }
  }

  return { head, tail };
};

/**
 * Same parsing, filling, and element nesting as `renderInlineMarkup`, but
 * splits the result into a visible head and a hidden tail at a character
 * budget — the shared building block for "show more" on r-text.
 *
 * Counts what a reader sees after markers are gone and `fillText` has run:
 * Unicode code points, with consecutive internal whitespace (and `{br|}`)
 * counting once for the budget only. Leading and trailing whitespace —
 * including leading and trailing `{br|}` — do not count, so a trailing space
 * past the limit never invents a "show more" that reveals nothing, and leading
 * spaces never consume the budget or leave an empty-looking head.
 *
 * A whole `{{ … }}` placeholder token is never cut through: whitespace inside
 * it is not a cut point, and the first-word fallback keeps a word that
 * contains a token rather than slicing the token. Counting is unchanged;
 * only where a cut may land changes. Applied whether or not a filler ran —
 * after filling, only leftover literal tokens remain.
 *
 * Returns `undefined` when everything fits, when `limit` is not a positive
 * integer, or when a cut would leave the head or the tail with no
 * non-whitespace character.
 *
 * The head keeps whole words that fit; if the first word is longer than
 * `limit` and has no token, that word is cut at the budget. Whitespace at the
 * cut — including a break — starts the tail, so concatenating the two sides'
 * text matches the full rendering exactly.
 */
export function renderInlineMarkupSplit(
  text: string,
  limit: number,
  links: unknown,
  spanStyles?: unknown,
  fillText?: (words: string) => string
): { head: ReactNode; tail: ReactNode } | undefined {
  if (!isPositiveIntegerLimit(limit)) {
    return undefined;
  }

  const runs = prepareInlineMarkupRuns(text, links, spanStyles, fillText);
  const units = flattenUnits(runs);

  if (visibleLength(units) <= limit) {
    return undefined;
  }

  const insidePlaceholder = markUnitsInsidePlaceholderTokens(runs, units);
  const headExclusiveEnd = findHeadExclusiveEnd(units, limit, insidePlaceholder);

  if (
    !hasNonWhitespace(units, 0, headExclusiveEnd) ||
    !hasNonWhitespace(units, headExclusiveEnd, units.length)
  ) {
    return undefined;
  }

  const { head, tail } = splitPreparedRuns(runs, units, headExclusiveEnd);

  return {
    head: renderPreparedMarkupRuns(head),
    tail: renderPreparedMarkupRuns(tail),
  };
}
